import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";

import { toast } from "@performyard/py-components";

import {
	useAddUsersToSelection,
	useRemoveUsersFromSelection,
	useUserSelectionCount,
	useUserSelectionUsers,
	userSelectionUsersQuery,
} from "../../api/hooks/defs/user-selection";

import type {
	AddUsersToSelectionProps,
	GetUserSelectionUsersProps,
	RemoveUsersFromSelectionProps,
	UserSelectionMutationResponse,
	UserSelectionUsersResponse,
} from "../../api/services/defs/user-selection";

export const useEmployeeSelectionObject = (props: GetUserSelectionUsersProps) => {
	const queryClient = useQueryClient();
	const [initializedLoad, setInitializedLoad] = useState(false);
	const [removedUsers, setRemovedUsers] = useState<UserSelectionUsersResponse["users"]>([]);
	const {
		selectionObjectID,
		page,
		fieldIds,
		letter,
		limitToDirectReports,
		exclusionList,
		usersToExcludeSelectionId,
		excludeCurrentUser,
		filters,
		sortField,
		sortDirection,
		searchTerm,
		limitSelected,
		limit = 25,
		onRemoveSuccess,
		onRemoveError,
	} = props;

	const queryProps = {
		fieldIds,
		letter,
		limitToDirectReports,
		exclusionList,
		usersToExcludeSelectionId,
		excludeCurrentUser,
		filters,
		sortField,
		sortDirection,
		limit,
		searchTerm,
	};

	// Create consistent props object for query key matching
	const currentQueryProps = { selectionObjectID, page, limitSelected, ...queryProps };
	const queryKey = userSelectionUsersQuery(currentQueryProps).queryKey;
	const queryKeyBase = queryKey[0]; // "user-selection-users"

	const {
		data,
		isLoading,
		isFetching,
		isPlaceholderData,
		refetch: refetchUsers,
	} = useUserSelectionUsers(currentQueryProps);

	const {
		data: countData,
		isLoading: isLoadingCount,
		refetch: refetchCount,
	} = useUserSelectionCount(selectionObjectID);

	const { mutate: addUsersToSelection, isPending: isAddingUsersToSelection } = useAddUsersToSelection({
		onMutate: (variables: AddUsersToSelectionProps) => {
			const updateCache = (oldData?: UserSelectionUsersResponse) => {
				if (!oldData) return oldData;

				const updates: Record<string, boolean> = {};

				// Handle individual users
				if (variables?.users) {
					variables.users.forEach((userId) => {
						updates[userId] = true;
					});
				}

				// Handle groups - mark all users in those groups as selected
				if (variables?.groups && variables.groups.length > 0 && oldData.users) {
					const groupCustomField = oldData.custom_fields.find(
						(field) => field.name === "Group" && field.type === "department"
					);
					if (groupCustomField) {
						oldData.users.forEach((user) => {
							if (variables?.groups?.includes(user.data_fields[groupCustomField._id.toString()].value)) {
								updates[user._id] = true;
							}
						});
					}
				}

				// Handle all users
				if (variables?.allUsers && oldData.users) {
					oldData.users.forEach((user) => {
						updates[user._id] = true;
					});
				}

				// Only update selected_users while preserving all other references
				oldData.selected_users = {
					...oldData.selected_users,
					...updates,
				};

				return oldData;
			};

			queryClient.setQueriesData<UserSelectionUsersResponse>(
				{ queryKey: [queryKeyBase, selectionObjectID] },
				updateCache
			);
		},
		onError: (_error, variables: AddUsersToSelectionProps) => {
			// Rollback the optimistic update
			queryClient.setQueryData(queryKey, (oldData: UserSelectionUsersResponse) => {
				if (!oldData) return oldData;

				const updatedSelectedUsers = { ...oldData.selected_users };

				// Remove individual users
				if (variables?.users) {
					variables.users.forEach((userId) => {
						delete updatedSelectedUsers[userId];
					});
				}

				// Remove all users if it was an "all users" selection
				if (variables?.allUsers && oldData.users) {
					oldData.users.forEach((user) => {
						delete updatedSelectedUsers[user._id];
					});
				}

				// Only update selected_users while preserving all other references
				oldData.selected_users = updatedSelectedUsers;

				toast.error("There was an error adding users to the selection");
				queryClient.invalidateQueries({ queryKey: queryKey });

				// Also invalidate prefetched entries
				queryClient.invalidateQueries({
					queryKey: ["user-selection-users", selectionObjectID],
					exact: false,
				});

				return oldData;
			});
		},
		onSuccess: (_data: UserSelectionMutationResponse, variables: AddUsersToSelectionProps) => {
			if (variables?.groups || variables?.allUsers) {
				refetchUsers();
			}
			refetchCount();
		},
	});

	const { mutate: removeUsersFromSelection, isPending: isRemovingUsersFromSelection } = useRemoveUsersFromSelection({
		onMutate: (variables: RemoveUsersFromSelectionProps) => {
			const updateCache = (oldData?: UserSelectionUsersResponse) => {
				if (!oldData) return oldData;

				let updatedSelectedUsers = { ...oldData.selected_users };

				// Remove users from selection
				variables?.users?.forEach((userId) => {
					delete updatedSelectedUsers[userId];
				});

				// Remove all users if it was an "all users" selection
				if (variables?.allUsers && oldData.users) {
					updatedSelectedUsers = {};
				}

				if (limitSelected) {
					oldData.users = oldData.users.filter((user) => updatedSelectedUsers[user._id]);
					setRemovedUsers(oldData.users.filter((user) => !updatedSelectedUsers[user._id]));
				}

				// Only update selected_users while preserving all other references
				oldData.selected_users = updatedSelectedUsers;

				return oldData;
			};

			queryClient.setQueriesData<UserSelectionUsersResponse>(
				{ queryKey: [queryKeyBase, selectionObjectID] },
				updateCache
			);
		},
		onError: (error, variables: RemoveUsersFromSelectionProps) => {
			// Rollback the optimistic update - add users back
			queryClient.setQueryData(queryKey, (oldData: UserSelectionUsersResponse) => {
				if (!oldData) return oldData;

				const rollbackUpdates: Record<string, boolean> = {};
				variables?.users?.forEach((userId) => {
					rollbackUpdates[userId] = true;
				});

				// Only update selected_users while preserving all other references
				oldData.selected_users = {
					...oldData.selected_users,
					...rollbackUpdates,
				};

				if (limitSelected) {
					oldData.users.push(...removedUsers);
					setRemovedUsers([]);
				}

				toast.error("There was an error removing users from the selection");
				queryClient.invalidateQueries({ queryKey: queryKey });

				// Also invalidate prefetched entries
				queryClient.invalidateQueries({
					queryKey: ["user-selection-users", selectionObjectID],
					exact: false,
				});

				return oldData;
			});
			toast.error("There was an error removing users from the selection");
			onRemoveError?.(error);
		},
		onSuccess: (data: UserSelectionMutationResponse, variables: RemoveUsersFromSelectionProps) => {
			if (variables?.allUsers) {
				refetchUsers();
			}
			refetchCount();
			setRemovedUsers([]);
			onRemoveSuccess?.(data);
		},
	});

	useEffect(() => {
		if (!isLoading && !initializedLoad) {
			setInitializedLoad(true);
		}
	}, [isLoading]);

	return {
		data,
		usersList: data?.users || [],
		departments: data?.departments,
		custom_fields: data?.custom_fields,
		display_user_map: data?.display_user_map,
		pageCount: data?.page_count ?? 1,
		isLoading,
		isFetching,
		isPlaceholderData,
		addUsersToSelection,
		isAddingUsersToSelection,
		removeUsersFromSelection,
		isRemovingUsersFromSelection,
		count: countData?.count,
		isLoadingCount,
		initializedLoad,
		refetchUsers,
	};
};

export { useEmployeeSelectionObjectInfinite } from "./useEmployeeSelectionObjectInfinite";
